import { DatePipe } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { BusinessSearchResult } from '../../interfaces/business-search-results/business-search-result';
import { BusinessSearchResultRepositoryService } from '../../services/business-search-results/business-search-result-repository.service';
import { FiltersComponent } from '../../ui-common/filters-component';
import { OrderManagerService } from '../../services/order-manager/order-manager.service';
import { SelectOption } from '../../interfaces/select-option';
import { HttpParams } from '@angular/common/http';
import { first, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { eOrderStatusType, eOrderType } from '../../enums';
import { Order } from '../../interfaces/orders/order';
import { TranslocoService } from '@jsverse/transloco';

export interface BusinessSearchResultsFilterLabels {
  title: string;
  name: string;
  number: string;
  status: string;
  jurisdiction: string;
  filter: string;
  clearFilters: string;
}

@Component({
    selector: 'lib-business-search-results-filter',
    templateUrl: './business-search-results-filter.component.html',
    styleUrls: ['./business-search-results-filter.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BusinessSearchResultsFilterComponent extends FiltersComponent<BusinessSearchResult> implements OnInit, OnDestroy {
  private readonly transloco = inject(TranslocoService);
  @Input() order?: Order;
  @Input() jurisdictions: SelectOption[] = [];
  @Input() types: SelectOption[] = [];
  @Input() statuses: SelectOption[] = [];
  @Input() descriptions: SelectOption[] = [];
  @Input() jurisdictionStatuses: SelectOption[] = [];
  
  @Input() public showNameFilter = true;
  @Input() public showNumberFilter = true;
  @Input() public showJurisdictionFilter = true;
  @Input() public showJurisdictionStatusFilter = true;
  @Input() public showStatusFilter = true;
  @Input() public showStatusDateFromFilter = true;
  @Input() public showStatusDateToFilter = true;
  @Input() public showCreationDateFromFilter = true;
  @Input() public showCreationDateToFilter = true;
  @Input() public showCompanyTypeFilter = true;
  @Input() public showDescriptionFilter = true;
  @Input() public showHiddenFilter = false;
  private _labels?: Partial<BusinessSearchResultsFilterLabels>;
  @Input() set labels(value: Partial<BusinessSearchResultsFilterLabels> | null | undefined) { this._labels = value ?? undefined; }
  /** Caller-provided labels override the translated defaults. */
  get labels(): BusinessSearchResultsFilterLabels {
    const defaults: BusinessSearchResultsFilterLabels = {
      title: this.transloco.translate('common.labels.filters'),
      name: this.transloco.translate('common.labels.name'),
      number: this.transloco.translate('common.labels.number'),
      status: this.transloco.translate('common.labels.status'),
      jurisdiction: this.transloco.translate('common.labels.jurisdiction'),
      filter: this.transloco.translate('common.labels.filter'),
      clearFilters: this.transloco.translate('common.labels.clearFilters'),
    };
    return { ...defaults, ...this._labels } as BusinessSearchResultsFilterLabels;
  }

  private onDestroy$ = new Subject<void>();
  
  constructor(
    protected orderManager: OrderManagerService,
    businessSearchResultsRepo: BusinessSearchResultRepositoryService,
    formBuilder: FormBuilder,
    datePipe: DatePipe) {
      super(
        businessSearchResultsRepo,
        formBuilder,
        datePipe
      )
  }

  ngOnInit() {
    this._defaultParams = new HttpParams().set("SortColumn", "Score").set("SortDirection", "Asc").set("ShowResult", true);

    this._filterForm = this.formBuilder.group({
      name: [null],
      number: [null],
      jurisdiction: [null],
      jurisdictionStatus: [null],
      status: [null],
      statusDateFrom: [null],
      statusDateTo: [null],
      creationDateFrom: [null],
      creationDateTo: [null],
      companyType: [null],
      description: [null],
      showResult: [true],
      sortColumn: ["Score"],
      sortDirection: ["Asc"]
    });
    
    if (!this.showNameFilter) {
      this._filterForm.removeControl('name');
    }
    
    if (!this.showNumberFilter) {
      this._filterForm.removeControl('number');
    }
    
    if (!this.showJurisdictionFilter) {
      this._filterForm.removeControl('jurisdiction');
    }
    
    if (!this.showJurisdictionStatusFilter) {
      this._filterForm.removeControl('jurisdictionStatus');
    }
    
    if (!this.showStatusFilter) {
      this._filterForm.removeControl('status');
    }
    
    if (!this.showStatusDateFromFilter) {
      this._filterForm.removeControl('statusDateFrom');
    }
    
    if (!this.showStatusDateToFilter) {
      this._filterForm.removeControl('statusDateTo');
    }
    
    if (!this.showCreationDateFromFilter) {
      this._filterForm.removeControl('creationDateFrom');
    }
    
    if (!this.showCreationDateToFilter) {
      this._filterForm.removeControl('creationDateTo');
    }
    
    if (!this.showCompanyTypeFilter) {
      this._filterForm.removeControl('companyType');
    }
    
    if (!this.showDescriptionFilter) {
      this._filterForm.removeControl('description');
    }

    const orderSubscribable = this.order ? of(this.order) : this.orderManager.currentOrder;

    orderSubscribable.pipe(
      takeUntil(this.onDestroy$),
      first(order => order !== undefined && order.orderTypeID === eOrderType.businessSearch && order.orderStatusTypeID === eOrderStatusType.complete),
      tap(order => this.repo.populateBaseLink([order.id])),
      switchMap(order => (this.repo as BusinessSearchResultRepositoryService).getBusinessSearchResultsFilters(order.id))
    ).subscribe(filters => {
      this.jurisdictions = filters.jurisdictions;

      if (this.jurisdictions.some(jurisdiction => jurisdiction.value === '_F')) {
        var index = this.jurisdictions.findIndex(jurisdiction => jurisdiction.value == '_F');
        this.jurisdictions.at(index)!.label = "FED";
      }

      this.types = filters.companyTypes;
      this.statuses = filters.statuses;
      this.descriptions = filters.descriptions;
      this.jurisdictionStatuses = filters.jurisdictionStatuses;
    });
  }

  ngOnDestroy(): void {
    this.repo.clearFilters();
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
